import { AuthAPI } from '../api/auth';
import { ApiError, apiGet, apiPost, apiRequest } from '../lib/http';
import {
  PushPublicKeyResponse,
  PushSubscribePayload,
  PushSubscriptionsResponse,
  PushUnsubscribePayload,
} from '../types/api/auth/push';

function base64UrlToApplicationServerKey(base64Url: string): ArrayBuffer {
  const padding = '='.repeat((4 - (base64Url.length % 4)) % 4);
  const base64 = (base64Url + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);

  const bytes = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) {
    bytes[i] = raw.charCodeAt(i);
  }

  // Force echtes ArrayBuffer für PushManager-Typen
  return bytes.buffer as ArrayBuffer;
}

export class PushService {
  isSupported(): boolean {
    return 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  }

  async ensurePermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) return 'denied';
    if (Notification.permission === 'granted') return 'granted';
    if (Notification.permission === 'denied') return 'denied';
    return Notification.requestPermission();
  }

  async ensureSubscriptionRegistered(): Promise<void> {
    if (!this.isSupported()) return;

    const permission = await this.ensurePermission();
    if (permission !== 'granted') return;

    const keyRes = await apiGet<PushPublicKeyResponse>(AuthAPI.pushPublicKey());
    const enabled = keyRes.data?.enabled;
    const publicKey = keyRes.data?.publicKey;

    if (!enabled || !publicKey) return;

    const reg = await navigator.serviceWorker.ready;
    const existing = await reg.pushManager.getSubscription();

    const sub =
      existing ??
      (await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: base64UrlToApplicationServerKey(publicKey),
      }));

    const json = sub.toJSON();
    if (!json.endpoint || !json.keys?.p256dh || !json.keys?.auth) return;

    const payload: PushSubscribePayload = {
      endpoint: json.endpoint,
      keys: {
        p256dh: json.keys.p256dh,
        auth: json.keys.auth,
      },
    };

    await apiPost(AuthAPI.pushSubscriptions(), payload);
  }

  async unsubscribeCurrentDevice(): Promise<void> {
    if (!this.isSupported()) return;

    const reg = await navigator.serviceWorker.ready;
    const sub = await reg.pushManager.getSubscription();
    if (!sub) return;

    const payload: PushUnsubscribePayload = { endpoint: sub.endpoint };

    try {
      await apiRequest(AuthAPI.pushSubscriptions(), {
        method: 'DELETE',
        json: payload,
      });
    } catch (err) {
      if (!(err instanceof ApiError) || err.status !== 404) {
        throw err;
      }
    }
    await sub.unsubscribe();
  }

  async hasActiveBackendSubscriptions(): Promise<boolean> {
    const res = await apiGet<PushSubscriptionsResponse>(AuthAPI.pushSubscriptions());
    return Array.isArray(res.data?.subscriptions) && res.data.subscriptions.some((s) => s.isActive);
  }
}

export const pushService = new PushService();
